import React, { FC } from 'react';

import classnames from 'classnames';
import { Text } from 'mintel-react-ui';

import { SeriesItem, Section, TranslationResults } from '/types';
import useTracking from '/utils/useTracking';

import styles from './ExpandedNav.scss';
import NavItem from './NavItem';
import SeriesNav from './SeriesNav';
import TranslationsNav from './TranslationsNav';
import RelatedContentNav, {
  RelatedContent,
} from './RelatedContentNav/RelatedContentNav';

type ExpandedNavProps = {
  currentPosition: number;
  expandedIcon: string;
  navTitles: {
    [key: string]: string;
  };
  show: boolean;
  sections: Section[];
  seriesData: SeriesItem[];
  title: string;
  isInSection: (section: Section) => boolean;
  scrollTo: (index: number) => void;
  translations: TranslationResults;
  relatedContent?: RelatedContent;
};
const ExpandedNav: FC<ExpandedNavProps> = ({
  currentPosition,
  expandedIcon,
  isInSection,
  navTitles,
  show,
  sections,
  scrollTo,
  seriesData,
  title,
  translations,
  relatedContent,
}) => {
  const { trackContentNavigate } = useTracking();

  const navItems = sections.map((section: Section) => {
    const subsections = section.subsections
      ? section.subsections.map((subsection: Section) => (
          <NavItem
            active={currentPosition === subsection.index}
            passed={currentPosition >= subsection.index}
            section={subsection}
            isSubsection
            text={subsection.title}
            scrollTo={scrollTo}
            key={subsection.id}
            onNavigate={(): void =>
              trackContentNavigate(
                title,
                subsection.id,
                subsection.title,
                section.title,
              )
            }
            expandedNav
          />
        ))
      : null;

    return (
      <div key={section.id}>
        <NavItem
          active={currentPosition === section.index}
          passed={currentPosition >= section.index}
          section={section}
          text={section.title}
          scrollTo={scrollTo}
          isInSection={isInSection}
          onNavigate={(): void =>
            trackContentNavigate(title, section.id, section.title)
          }
          expandedNav
        />
        {subsections}
      </div>
    );
  });

  return (
    <div
      id={`${expandedIcon.replace(/\s+/g, '-')}-nav-content`}
      className={classnames(styles['expanded-nav'], { [styles.show]: show })}
    >
      {translations?.length > 1 && expandedIcon === navTitles.translation && (
        <TranslationsNav translations={translations} />
      )}
      {expandedIcon === navTitles.toc && (
        <>
          <div className={styles['outer-item']}>
            <Text.Heading5 darkBg>{navTitles.toc}</Text.Heading5>
          </div>
          {navItems}
        </>
      )}
      {!!seriesData.length && expandedIcon === navTitles.series && (
        <SeriesNav
          data={seriesData}
          now={new Date()}
          title={navTitles.series}
        />
      )}
      {expandedIcon === navTitles.relatedContent && (
        <RelatedContentNav data={relatedContent} />
      )}
    </div>
  );
};

export default ExpandedNav;
